/**
 * CreateOpportunityDialog — formulário modal para criação de nova oportunidade
 * (Req 1..4, DD-004, ADR-0009).
 *
 * Campos mínimos obrigatórios (OP-ERR-001..004):
 * - Título (OP-ERR-001/011)
 * - Conta: <select> populado via accountsRepo.listAccounts
 * - Estágio: <select> populado via platformRepo.listStages(buId) — ordena por position
 * - Canal de origem: <select> populado via platformRepo.listOriginChannels(buId)
 * - Responsável: <select> populado via platformRepo.listBuMembers(buId)
 *   — pré-selecionado com usuário logado quando presente na lista
 * - Valor de setup e mensal (centavos inteiros, RNF 11)
 * - Duração em meses
 *
 * buId: derivado do `useActiveScope().businessUnitId` (escopo ativo) com
 * fallback para o `business_unit_id` do JWT (ADR-0009).
 *
 * Padrão de POST: `apiClient.create` via `createOpportunity` do repo, seguido
 * de invalidação do cache do board pelo chamador via `onSuccess`.
 */

import { useState, useId, useCallback, useEffect, type FormEvent } from "react";
import { Modal, Button, Input, Field } from "@azim-crm/ui-components";
import { Icon } from "@azim-crm/icons";
import { useAuth } from "../../app/AuthProvider.js";
import { useActiveScope } from "../../app/providers/ActiveScopeProvider.js";
import { useTenantQuery } from "../../app/providers/QueryProvider.js";
import { decodeTokenClaims } from "../../shared/auth/tokenClaims.js";
import { createOpportunity, type CreateOpportunityInput } from "../../data/repos/opportunitiesRepo.js";
import { listAccounts } from "../../data/repos/accountsRepo.js";
import {
  listStages,
  listBuMembers,
  listOriginChannels,
  listBusinessUnits,
} from "../../data/repos/platformRepo.js";
import { parseBRLInputToCents, maskCentsInput } from "../../shared/money/money.js";
import { mapOpportunityError } from "../../shared/errors/opportunityErrors.js";
import styles from "../accounts/CreateAccountDialog.module.css";

// ---------------------------------------------------------------------------
// Mapeamento de erros da API para mensagens amigáveis
// ---------------------------------------------------------------------------

function mapApiError(err: unknown): string {
  return mapOpportunityError(err, { fallback: "Erro ao criar oportunidade. Tente novamente." });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreateOpportunityDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /**
   * Chamado após criação bem-sucedida. O chamador deve invalidar o cache do board.
   * Recebe o id da oportunidade criada.
   */
  readonly onSuccess: (id?: string) => void;
  /**
   * Função de criação injetável para testes. Default: `createOpportunity` do repo.
   */
  readonly onCreate?: (input: CreateOpportunityInput) => Promise<{ id: string }>;
}

// ---------------------------------------------------------------------------
// Conversão monetária: ver `shared/money` (parseBRLInputToCents / maskCentsInput).
// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function CreateOpportunityDialog({
  open,
  onClose,
  onSuccess,
  onCreate = createOpportunity,
}: CreateOpportunityDialogProps) {
  const titleId = useId();
  const { user } = useAuth();
  const { businessUnitId } = useActiveScope();

  // Deriva o ownerId (usuário logado) e buId do escopo ativo ou do JWT
  const claims = user?.token ? decodeTokenClaims(user.token) : null;
  const loggedUserId = claims ? (user?.id ?? "") : "";
  const effectiveBuId =
    businessUnitId ?? claims?.business_unit_id ?? "";

  // ---------------------------------------------------------------------------
  // Dados para os selects — via useTenantQuery (cacheados, sem loop)
  // ---------------------------------------------------------------------------
  const { data: accountsPage } = useTenantQuery(
    "opportunity.create.accounts",
    () => listAccounts({ page: 1, pageSize: 100 }),
    { enabled: open },
  );
  const accounts = accountsPage?.items ?? [];

  const {
    data: stages = [],
    isError: stagesError,
    isLoading: stagesLoading,
    refetch: refetchStages,
  } = useTenantQuery(
    "opportunity.create.stages",
    () => listStages(effectiveBuId),
    { params: effectiveBuId, enabled: open && !!effectiveBuId },
  );
  // Ordena por position (crescente)
  const sortedStages = [...stages].sort((a, b) => a.position - b.position);

  const {
    data: channels = [],
    isError: channelsError,
    isLoading: channelsLoading,
    refetch: refetchChannels,
  } = useTenantQuery(
    "opportunity.create.channels",
    () => listOriginChannels(effectiveBuId),
    { params: effectiveBuId, enabled: open && !!effectiveBuId },
  );

  // Nome da BU do escopo ativo. O escopo só carrega o GUID (claim do JWT); o
  // rótulo legível vem de GET /v1/business-units, mesmo caminho já usado por
  // CreateAccountDialog.
  const { data: businessUnits = [] } = useTenantQuery(
    "opportunity.create.businessUnits",
    listBusinessUnits,
    { enabled: open },
  );
  const activeBuName =
    businessUnits.find((bu) => bu.id === effectiveBuId)?.name ?? null;

  const { data: members = [] } = useTenantQuery(
    "opportunity.create.members",
    () => listBuMembers(effectiveBuId),
    { params: effectiveBuId, enabled: open && !!effectiveBuId },
  );

  // ---------------------------------------------------------------------------
  // Estado do formulário
  // ---------------------------------------------------------------------------
  const [title, setTitle] = useState("");
  const [accountId, setAccountId] = useState("");
  const [stageId, setStageId] = useState("");
  const [originChannelId, setOriginChannelId] = useState("");
  const [ownerId, setOwnerId] = useState(loggedUserId);
  const [valorSetup, setValorSetup] = useState("");
  const [valorMensal, setValorMensal] = useState("");
  const [duracaoMeses, setDuracaoMeses] = useState("12");
  const [probabilidade, setProbabilidade] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");

  // Estado de UI
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pré-seleciona o primeiro estágio quando a lista carrega
  useEffect(() => {
    if (sortedStages.length > 0 && !stageId && sortedStages[0]) {
      setStageId(sortedStages[0].id);
    }
  }, [sortedStages, stageId]);

  // Pré-seleciona o primeiro canal quando a lista carrega
  useEffect(() => {
    if (channels.length > 0 && !originChannelId && channels[0]) {
      setOriginChannelId(channels[0].id);
    }
  }, [channels, originChannelId]);

  // Pré-seleciona o responsável: usuário logado se presente na lista; caso
  // contrário, o primeiro membro. Funciona mesmo quando loggedUserId não resolve
  // (antes, sem usuário logado, nenhum responsável era pré-selecionado).
  useEffect(() => {
    const firstMember = members[0];
    if (!firstMember || ownerId) return;
    const loggedInList = !!loggedUserId && members.some((m) => m.userId === loggedUserId);
    setOwnerId(loggedInList ? loggedUserId : firstMember.userId);
  }, [members, loggedUserId, ownerId]);

  // ---------------------------------------------------------------------------
  // Reset ao fechar
  // ---------------------------------------------------------------------------
  const handleClose = useCallback(() => {
    setTitle("");
    setAccountId("");
    setStageId("");
    setOriginChannelId("");
    setOwnerId(loggedUserId);
    setValorSetup("");
    setValorMensal("");
    setDuracaoMeses("12");
    setProbabilidade("");
    setExpectedCloseDate("");
    setFieldErrors({});
    setApiError(null);
    setSubmitting(false);
    onClose();
  }, [onClose, loggedUserId]);

  // ---------------------------------------------------------------------------
  // Validação client-side
  // ---------------------------------------------------------------------------
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!title.trim()) errors["title"] = "O título é obrigatório.";
    if (!accountId.trim()) errors["accountId"] = "A conta é obrigatória.";
    if (!stageId.trim()) errors["stageId"] = "O estágio é obrigatório.";
    if (!originChannelId.trim()) errors["originChannelId"] = "O canal de origem é obrigatório.";
    if (!ownerId.trim()) errors["ownerId"] = "O responsável é obrigatório.";
    const valorMensalCents = parseBRLInputToCents(valorMensal);
    const duracao = parseInt(duracaoMeses, 10);
    if (valorMensalCents > 0 && (isNaN(duracao) || duracao <= 0)) {
      errors["duracaoMeses"] = "Duração em meses é obrigatória quando há valor mensal.";
    }
    if (probabilidade.trim() !== "") {
      const prob = parseInt(probabilidade, 10);
      if (isNaN(prob) || prob < 0 || prob > 100) {
        errors["probabilidade"] = "Probabilidade deve ser entre 0 e 100.";
      }
    }
    return errors;
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const errors = validate();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      setApiError(null);
      setSubmitting(true);

      try {
        const setupCents = parseBRLInputToCents(valorSetup);
        const mensalCents = parseBRLInputToCents(valorMensal);
        const duracao = parseInt(duracaoMeses, 10);
        const prob = probabilidade.trim() !== "" ? parseInt(probabilidade, 10) : undefined;

        const input: CreateOpportunityInput = {
          title: title.trim(),
          accountId: accountId.trim(),
          buId: effectiveBuId,
          stageId: stageId.trim(),
          originChannelId: originChannelId.trim(),
          ownerId: ownerId.trim(),
          valorSetupCents: setupCents,
          valorMensalCents: mensalCents,
          duracaoMeses: duracao,
          ...(prob !== undefined && { probabilidade: prob }),
          ...(expectedCloseDate.trim() !== "" && { expectedCloseDate: expectedCloseDate.trim() }),
        };

        const { id } = await onCreate(input);
        handleClose();
        onSuccess(id);
      } catch (err) {
        setApiError(mapApiError(err));
      } finally {
        setSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, accountId, stageId, originChannelId, ownerId, valorSetup, valorMensal, duracaoMeses, probabilidade, expectedCloseDate, effectiveBuId, onCreate, handleClose, onSuccess],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Modal
      open={open}
      onClose={handleClose}
      width={560}
      aria-labelledby={titleId}
    >
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          Nova oportunidade
        </h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Fechar dialog"
        >
          <Icon name="x" size={18} />
        </button>
      </div>

      <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
        <div className={styles.body}>
          <div className={styles.fieldset}>

            {/* Título */}
            <Field
              label="Título"
              htmlFor="co-title"
              required
              {...(fieldErrors["title"] ? { error: fieldErrors["title"] } : {})}
            >
              <Input
                id="co-title"
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); if (fieldErrors["title"]) setFieldErrors((p) => { const n = { ...p }; delete n["title"]; return n; }); }}
                placeholder="Ex.: Projeto de implantação CRM"
                autoFocus
                hasError={!!fieldErrors["title"]}
                aria-required="true"
              />
            </Field>

            {/* Conta — select */}
            <Field
              label="Conta"
              htmlFor="co-account"
              required
              {...(fieldErrors["accountId"] ? { error: fieldErrors["accountId"] } : {})}
            >
              <div className={styles.selectWrapper}>
                <select
                  id="co-account"
                  className={`${styles.select}${fieldErrors["accountId"] ? ` ${styles.hasError}` : ""}`}
                  value={accountId}
                  onChange={(e) => { setAccountId(e.target.value); if (fieldErrors["accountId"]) setFieldErrors((p) => { const n = { ...p }; delete n["accountId"]; return n; }); }}
                  aria-required="true"
                  aria-label="Conta"
                >
                  <option value="">Selecione uma conta…</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <span className={styles.selectChevron} aria-hidden="true">
                  <Icon name="chevronDown" size={14} />
                </span>
              </div>
            </Field>

            {/* Estágio — select */}
            <Field
              label="Estágio"
              htmlFor="co-stage"
              required
              {...(fieldErrors["stageId"] ? { error: fieldErrors["stageId"] } : {})}
            >
              <div className={styles.selectWrapper}>
                <select
                  id="co-stage"
                  className={`${styles.select}${fieldErrors["stageId"] ? ` ${styles.hasError}` : ""}`}
                  value={stageId}
                  onChange={(e) => { setStageId(e.target.value); if (fieldErrors["stageId"]) setFieldErrors((p) => { const n = { ...p }; delete n["stageId"]; return n; }); }}
                  aria-required="true"
                  aria-label="Estágio"
                >
                  <option value="">
                    {stagesLoading ? "Carregando estágios…" : "Selecione um estágio…"}
                  </option>
                  {sortedStages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <span className={styles.selectChevron} aria-hidden="true">
                  <Icon name="chevronDown" size={14} />
                </span>
              </div>
              {/* Falha ao carregar estágios: mensagem acionável + retry (não trava
                  silenciosamente o formulário quando o endpoint de stages falha). */}
              {stagesError && (
                <div role="alert" className={styles.fieldError}>
                  Não foi possível carregar os estágios desta BU.{" "}
                  <button
                    type="button"
                    onClick={() => { void refetchStages(); }}
                    className={styles.fieldErrorRetry}
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
            </Field>

            {/* Canal de origem — select */}
            <Field
              label="Canal de origem"
              htmlFor="co-channel"
              required
              {...(fieldErrors["originChannelId"] ? { error: fieldErrors["originChannelId"] } : {})}
            >
              <div className={styles.selectWrapper}>
                <select
                  id="co-channel"
                  className={`${styles.select}${fieldErrors["originChannelId"] ? ` ${styles.hasError}` : ""}`}
                  value={originChannelId}
                  onChange={(e) => { setOriginChannelId(e.target.value); if (fieldErrors["originChannelId"]) setFieldErrors((p) => { const n = { ...p }; delete n["originChannelId"]; return n; }); }}
                  aria-required="true"
                  aria-label="Canal de origem"
                >
                  <option value="">
                    {channelsLoading ? "Carregando canais…" : "Selecione um canal…"}
                  </option>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <span className={styles.selectChevron} aria-hidden="true">
                  <Icon name="chevronDown" size={14} />
                </span>
              </div>
              {/* Falha ao carregar canais: antes caía em lista vazia silenciosa, e o
                  formulário travava sem dizer por quê (o campo é obrigatório). */}
              {channelsError && (
                <div role="alert" className={styles.fieldError}>
                  Não foi possível carregar os canais de origem desta BU.{" "}
                  <button
                    type="button"
                    onClick={() => { void refetchChannels(); }}
                    className={styles.fieldErrorRetry}
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
              {/* Lista vazia (sem erro): aponta o caminho do cadastro em vez de
                  deixar o usuário diante de um seletor sem opções. */}
              {!channelsLoading && !channelsError && channels.length === 0 && (
                <div role="status" className={styles.fieldError}>
                  Nenhum canal de origem cadastrado nesta BU. Cadastre em Plataforma → Canais de
                  origem.
                </div>
              )}
            </Field>

            {/* Responsável — select */}
            <Field
              label="Responsável"
              htmlFor="co-owner"
              required
              {...(fieldErrors["ownerId"] ? { error: fieldErrors["ownerId"] } : {})}
            >
              <div className={styles.selectWrapper}>
                <select
                  id="co-owner"
                  className={`${styles.select}${fieldErrors["ownerId"] ? ` ${styles.hasError}` : ""}`}
                  value={ownerId}
                  onChange={(e) => { setOwnerId(e.target.value); if (fieldErrors["ownerId"]) setFieldErrors((p) => { const n = { ...p }; delete n["ownerId"]; return n; }); }}
                  aria-required="true"
                  aria-label="Responsável"
                >
                  <option value="">Selecione um responsável…</option>
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.displayName || m.email}
                    </option>
                  ))}
                </select>
                <span className={styles.selectChevron} aria-hidden="true">
                  <Icon name="chevronDown" size={14} />
                </span>
              </div>
            </Field>

            {/* Valores — layout em 2 colunas */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3, 0.75rem)" }}>
              <Field
                label="Valor de setup (R$)"
                htmlFor="co-setup"
                {...(fieldErrors["valorSetup"] ? { error: fieldErrors["valorSetup"] } : {})}
              >
                <Input
                  id="co-setup"
                  type="text"
                  inputMode="decimal"
                  value={valorSetup}
                  onChange={(e) => setValorSetup(maskCentsInput(e.target.value))}
                  placeholder="0,00"
                  hasError={!!fieldErrors["valorSetup"]}
                />
              </Field>

              <Field
                label="Valor mensal (R$)"
                htmlFor="co-mensal"
                {...(fieldErrors["valorMensal"] ? { error: fieldErrors["valorMensal"] } : {})}
              >
                <Input
                  id="co-mensal"
                  type="text"
                  inputMode="decimal"
                  value={valorMensal}
                  onChange={(e) => setValorMensal(maskCentsInput(e.target.value))}
                  placeholder="0,00"
                  hasError={!!fieldErrors["valorMensal"]}
                />
              </Field>
            </div>

            {/* Duração e probabilidade — layout em 2 colunas */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3, 0.75rem)" }}>
              <Field
                label="Duração (meses)"
                htmlFor="co-duracao"
                {...(fieldErrors["duracaoMeses"] ? { error: fieldErrors["duracaoMeses"] } : {})}
              >
                <Input
                  id="co-duracao"
                  type="number"
                  min="1"
                  value={duracaoMeses}
                  onChange={(e) => { setDuracaoMeses(e.target.value); if (fieldErrors["duracaoMeses"]) setFieldErrors((p) => { const n = { ...p }; delete n["duracaoMeses"]; return n; }); }}
                  placeholder="12"
                  hasError={!!fieldErrors["duracaoMeses"]}
                />
              </Field>

              <Field
                label="Probabilidade (%)"
                htmlFor="co-prob"
                {...(fieldErrors["probabilidade"] ? { error: fieldErrors["probabilidade"] } : {})}
              >
                <Input
                  id="co-prob"
                  type="number"
                  min="0"
                  max="100"
                  value={probabilidade}
                  onChange={(e) => { setProbabilidade(e.target.value); if (fieldErrors["probabilidade"]) setFieldErrors((p) => { const n = { ...p }; delete n["probabilidade"]; return n; }); }}
                  placeholder="Default do estágio"
                  hasError={!!fieldErrors["probabilidade"]}
                />
              </Field>
            </div>

            {/* Data de fechamento esperada */}
            <Field
              label="Data de fechamento esperada"
              htmlFor="co-close"
              {...(fieldErrors["expectedCloseDate"] ? { error: fieldErrors["expectedCloseDate"] } : {})}
            >
              <Input
                id="co-close"
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                hasError={!!fieldErrors["expectedCloseDate"]}
              />
            </Field>

            {/* Business Unit (read-only, escopo ativo) */}
            <div>
              <span className={styles.buLabel} id="co-bu-label">
                Business Unit (escopo ativo)
              </span>
              <div
                className={styles.buReadonly}
                aria-labelledby="co-bu-label"
                role="status"
              >
                {activeBuName ?? (effectiveBuId ? "Carregando…" : "Nenhuma BU no escopo")}
              </div>
              <span className={styles.buHint}>
                BU derivada do escopo ativo da sessão.
              </span>
            </div>
          </div>

          {/* Erro da API */}
          {apiError && (
            <div
              className={styles.apiError}
              role="alert"
              aria-live="assertive"
            >
              {apiError}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={submitting}
            aria-disabled={submitting}
          >
            {submitting ? "Criando…" : "Criar oportunidade"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
